import { ActionFunctionArgs, HeadersFunction, LoaderFunctionArgs, json, redirect } from '@remix-run/node';
import { Link, useLoaderData, useNavigation } from '@remix-run/react';
import { GetContentPageDocument } from '@theprintbar/graphql';
import { authenticator, getSession } from '@theprintbar/keystone';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonDay, getReturnToCookie, setReturnToCookie } from '@theprintbar/server';
import { WebRoutes, privateHeaderFunction } from '@theprintbar/shared';
import { BrandStar, ButtonSize, Checkbox, HoneypotForm, Input, PrimaryButton, SimpleCTA } from '@theprintbar/ui';
import { SingleSignOn } from '~/components/SingleSignOn';
import { getMigrationEmail, isMigrate } from '~/server/auth.server';
import { checkCartAndReturn } from '~/server/cart.server';
import { contentMeta } from '~/utils/contentMeta';
import { AuthContainer } from '../components/AuthContainer';
import { loginValidator } from './LoginSchema';

export const meta = contentMeta;
export const headers = privateHeaderFunction;

export const action = async ({ request }: ActionFunctionArgs) => {
	const returnTo = (await getReturnToCookie(request)) || WebRoutes.Account.Landing;
	const token = await authenticator.authenticate('local', request, {
		failureRedirect: WebRoutes.Login,
	});

	if (isMigrate(token)) {
		return redirect(`${WebRoutes.ForgotPassword}?migrated=${getMigrationEmail(token)}`);
	}

	return await checkCartAndReturn(request, token, returnTo);
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const user = await authenticator.isAuthenticated(request);

	if (user) {
		return redirect(WebRoutes.Account.Landing, {
			headers: {
				'X-Remix-Reload-Document': 'yes',
			},
		});
	}

	let error = (await getSession(request))?.get(authenticator.sessionErrorKey);

	const { contentPage } = await runSudoGraphql(GetContentPageDocument, { where: { slug: 'login' } });

	if (!contentPage) throw new Error('Content page not found');

	return json(
		{
			error: error?.message,
			contentPage,
		},
		await setReturnToCookie(request),
	);
};

export default function Login() {
	const pendingSubmit = useNavigation().state !== 'idle';
	const { error, contentPage } = useLoaderData<typeof loader>() || {};

	return (
		<AuthContainer content={contentPage}>
			<SingleSignOn hr="Or sign up with" />
			<HoneypotForm honeyField="date" validator={loginValidator} className="mt-8 space-y-6">
				<div className="space-y-6">
					<Input
						label="Email"
						name="email"
						type="email"
						disabled={pendingSubmit}
						autoComplete="email"
						inputClassName="bg-transparent !rounded-md !border-blue"
						required
					/>
					<Input
						label="Password"
						name="password"
						type="password"
						disabled={pendingSubmit}
						autoComplete="current-password"
						inputClassName="bg-transparent !rounded-md !border-blue"
						required
					/>

					<div className={'space-y-4'}>
						<div className="flex items-center justify-between">
							<Checkbox label="Remember me" name="remember" className="mb-0" defaultChecked disabled={pendingSubmit} />
							<Link to={WebRoutes.ForgotPassword} className="text-xs font-normal text-grey">
								Forgot your password?
							</Link>
						</div>

						<PrimaryButton
							type="submit"
							className="w-full flex items-center text-xl font-display font-light py-3.5 h-12 lg:px-6"
							disabled={pendingSubmit}
							spinner={pendingSubmit}
							icon={BrandStar}
							iconClass="fill-blue stroke-white"
							iconSize={ButtonSize.M}
							size={ButtonSize.L}
						>
							Login
						</PrimaryButton>
					</div>
					{error && <div className="text-red-500">{error}</div>}
				</div>
			</HoneypotForm>
			<SimpleCTA
				content={contentPage}
				containerClassName="mt-10"
				boxClassName="lg:px-10 lg:py-12 bg-bright-blue/20"
				boxBackground="#ECFAED"
				headerClassName="text-lg lg:text-xl"
			/>
		</AuthContainer>
	);
}
